import React, { useState, useMemo, useEffect } from "react";
import {
  Table, Row, Col, Form, InputGroup, Button,
  Pagination, Dropdown, Badge, Modal, Alert, Spinner, Toast, ToastContainer
} from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import { fetchSenderList, addSender, deleteSender, updateSenderStatus } from "../services/api";
import { useAuth } from "../hooks/useAuth";

// Types de Sender ID
const typesSenderID = ["Alphanumérique", "Numérique", "Court code"];

// Statuts disponibles
const statutsDisponibles = ["Approuvé", "En attente", "Non approuvé", "Suspendu", "En vérification"];

// Priorités
const prioritesDisponibles = ["Haute", "Moyenne", "Basse"];

export default function SenderIDManagement() {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('dateCreation');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedSenders, setSelectedSenders] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [senderIDs, setSenderIDs] = useState([]);
  const [selectedSender, setSelectedSender] = useState(null);
  const [selectedStatutFilter, setSelectedStatutFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const { user } = useAuth();

  const [notifications, setNotifications] = useState([]);

  const [senderForm, setSenderForm] = useState({
    senderId: '',
    type: 'Alphanumérique',
    description: '',
    priorite: 'Moyenne',
    campagne: ''
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [pendingSenderId, setPendingSenderId] = useState(null);

  // ============================================
  // NOTIFICATIONS
  // ============================================
  const addNotification = (message, type = 'success', duration = 5000) => {
    const id = Math.random().toString(36).substr(2, 9) + Date.now();
    setNotifications(prev => [{ id, message, type, show: true }, ...prev]);
    setTimeout(() => removeNotification(id), duration);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return 'bi-check-circle-fill';
      case 'error': return 'bi-exclamation-circle-fill';
      case 'warning': return 'bi-exclamation-triangle-fill';
      case 'info': return 'bi-info-circle-fill';
      default: return 'bi-bell-fill';
    }
  };

  const getNotificationBg = (type) => {
    switch (type) {
      case 'success': return 'success';
      case 'error': return 'danger';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'primary';
    }
  };

  // ============================================
  // HELPERS
  // ============================================
  const validateSenderID = (senderId, type) => {
    if (!senderId.trim()) return { isValid: false, message: "Veuillez saisir un Sender ID" };

    if (type === "Alphanumérique") {
      if (senderId.length < 3 || senderId.length > 11) {
        return { isValid: false, message: "Le Sender ID alphanumérique doit contenir entre 3 et 11 caractères" };
      }
      if (!/^[A-Z0-9_]+$/.test(senderId)) {
        return { isValid: false, message: "Le Sender ID ne doit contenir que des lettres majuscules, chiffres et underscores" };
      }
    } else if (type === "Numérique") {
      if (!/^241\d{8}$/.test(senderId)) {
        return { isValid: false, message: "Le Sender ID numérique doit commencer par 241 (11 chiffres total)" };
      }
    }

    return { isValid: true, message: "Sender ID valide" };
  };

  const getApiStatus = (displayStatus) => {
    switch (displayStatus) {
      case 'Approuvé': return 'YES';
      case 'Non approuvé': return 'NO';
      case 'Suspendu': return 'SUSPENDED';
      case 'En vérification': return 'PENDING';
      case 'En attente': return 'PENDING';
      default: return 'PENDING';
    }
  };

  const getDisplayStatus = (apiStatus) => {
    if (apiStatus === 'YES' || apiStatus === true || apiStatus === "active") return 'Approuvé';
    if (apiStatus === 'NO' || apiStatus === false || apiStatus === "inactive") return 'Non approuvé';
    if (apiStatus === 'SUSPENDED' || apiStatus === "suspend") return 'Suspendu';
    if (apiStatus === 'PENDING' || apiStatus === "pending") return 'En vérification';
    return 'En vérification';
  };

  const getStatusConfig = (statut) => {
    const config = {
      'Approuvé': { color: '#10b981', bg: '#d1fae5', icon: 'bi-check-circle-fill', label: 'Approuvé' },
      'En attente': { color: '#f59e0b', bg: '#fef3c7', icon: 'bi-clock-fill', label: 'En attente' },
      'En vérification': { color: '#f59e0b', bg: '#fef3c7', icon: 'bi-search', label: 'En vérification' },
      'Non approuvé': { color: '#ef4444', bg: '#fee2e2', icon: 'bi-x-circle-fill', label: 'Non approuvé' },
      'Suspendu': { color: '#6b7280', bg: '#f1f5f9', icon: 'bi-pause-circle-fill', label: 'Suspendu' }
    };
    return config[statut] || { color: '#6b7280', bg: '#f1f5f9', icon: 'bi-question-circle', label: statut };
  };

  // ============================================
  // CHARGEMENT
  // ============================================
  const loadSenders = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant. Veuillez vous reconnecter.');

      const data = await fetchSenderList(token);

      if (data && Array.isArray(data)) {
        const formattedSenders = data.map(sender => {
          const displayStatus = getDisplayStatus(sender.is_active);

          let type = "Alphanumérique";
          if (/^241\d{8}$/.test(sender.sender_id)) type = "Numérique";
          else if (sender.sender_id && sender.sender_id.length <= 5) type = "Court code";

          return {
            id: sender.id || sender.sender_id,
            senderId: sender.sender_id,
            sender_key: sender.sender_key,
            type: type,
            statut: displayStatus,
            dateCreation: sender.reg_date || new Date().toLocaleString('fr-FR'),
            dateApprobation: sender.is_active === "YES" || sender.is_active === true ? (sender.reg_date || new Date().toLocaleString('fr-FR')) : "",
            utilise: sender.usage_count || Math.floor(Math.random() * 1000),
            description: sender.description || `Sender ID ${sender.sender_id}`,
            priorite: prioritesDisponibles[Math.floor(Math.random() * prioritesDisponibles.length)],
            campagne: sender.campaign || "Campagnes diverses",
            business_id: sender.business_id,
            business_name: sender.business_name,
            is_active: sender.is_active
          };
        });

        setSenderIDs(formattedSenders);
      } else {
        throw new Error('Format de données invalide reçu de l\'API');
      }
    } catch (err) {
      console.error('❌ Erreur chargement senders:', err);
      setError(err.message || 'Erreur lors du chargement des senders');
      setSenderIDs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSenders(); }, []);

  // ============================================
  // CHANGEMENT STATUT
  // ============================================
  const prepareToggleStatus = (senderKey, currentApiStatus) => {
    const senderToUpdate = senderIDs.find(s => s.sender_key === senderKey);
    if (!senderToUpdate) return;

    setPendingSenderId(senderKey);
    setSelectedSender(senderToUpdate);

    const currentDisplayStatus = getDisplayStatus(currentApiStatus);
    let newApiStatus, newDisplayStatus;

    if (currentDisplayStatus === "Approuvé") {
      newApiStatus = 'NO';
      newDisplayStatus = "Non approuvé";
    } else {
      newApiStatus = 'YES';
      newDisplayStatus = "Approuvé";
    }

    setPendingStatusChange({
      oldStatus: currentDisplayStatus,
      newStatus: newDisplayStatus,
      apiStatus: newApiStatus
    });

    setShowStatusModal(true);
  };

  const executeToggleStatus = async () => {
    if (!pendingSenderId || !pendingStatusChange) return;

    try {
      setStatusLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      const senderToUpdate = senderIDs.find(s => s.sender_key === pendingSenderId);
      if (!senderToUpdate) throw new Error('Sender ID non trouvé');

      let actionValue;
      if (pendingStatusChange.newStatus === "Approuvé") actionValue = "active";
      else if (pendingStatusChange.newStatus === "Non approuvé") actionValue = "inactive";
      else if (pendingStatusChange.newStatus === "Suspendu") actionValue = "suspend";
      else actionValue = "pending";

      const requestBody = {
        action: actionValue,
        business_id: senderToUpdate.business_id || user?.business_id || 1017
      };

      const response = await updateSenderStatus(token, pendingSenderId, requestBody);

      if (response && response.code === 200) {
        setSenderIDs(prev =>
          prev.map(s =>
            s.sender_key === pendingSenderId
              ? {
                  ...s,
                  is_active: pendingStatusChange.apiStatus,
                  statut: pendingStatusChange.newStatus,
                  dateApprobation: pendingStatusChange.newStatus === "Approuvé" ? new Date().toLocaleString('fr-FR') : s.dateApprobation
                }
              : s
          )
        );

        addNotification(
          `Sender ID "${senderToUpdate.senderId}" ${pendingStatusChange.newStatus === "Approuvé" ? "activé" : "désactivé"} avec succès`,
          'success', 5000
        );
      } else {
        throw new Error(response?.message || 'Erreur lors de la mise à jour du statut');
      }
    } catch (err) {
      console.error('❌ Erreur statut:', err);
      addNotification(`Erreur: ${err.message}`, 'error', 6000);
    } finally {
      setStatusLoading(false);
      setShowStatusModal(false);
      setPendingStatusChange(null);
      setPendingSenderId(null);
      setSelectedSender(null);
    }
  };

  // ============================================
  // BULK TOGGLE
  // ============================================
  const handleBulkToggleStatus = async (activate = true) => {
    if (selectedSenders.length === 0) {
      addNotification("Veuillez sélectionner au moins un Sender ID", 'warning', 5000);
      return;
    }

    try {
      setStatusLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      const newApiStatus = activate ? 'YES' : 'NO';
      const newDisplayStatus = getDisplayStatus(newApiStatus);
      const actionValue = activate ? "active" : "inactive";

      const updatePromises = selectedSenders.map(senderKey => {
        const sender = senderIDs.find(s => s.sender_key === senderKey);
        if (!sender) return Promise.reject(new Error(`Sender ${senderKey} non trouvé`));

        const requestBody = {
          action: actionValue,
          business_id: sender.business_id || user?.business_id || 1017
        };

        return updateSenderStatus(token, senderKey, requestBody);
      });

      const results = await Promise.allSettled(updatePromises);
      const successfulUpdates = results.filter(r => r.status === 'fulfilled' && r.value?.code === 200);
      const failedUpdates = results.filter(r => r.status === 'rejected' || r.value?.code !== 200);

      if (successfulUpdates.length > 0) {
        setSenderIDs(prev =>
          prev.map(s =>
            selectedSenders.includes(s.sender_key)
              ? {
                  ...s,
                  is_active: newApiStatus,
                  statut: newDisplayStatus,
                  dateApprobation: newDisplayStatus === "Approuvé" ? new Date().toLocaleString('fr-FR') : s.dateApprobation
                }
              : s
          )
        );

        if (failedUpdates.length === 0) {
          addNotification(
            `${selectedSenders.length} Sender ID(s) ${activate ? "activé(s)" : "désactivé(s)"} avec succès`,
            'success', 5000
          );
        } else {
          addNotification(
            `${successfulUpdates.length} succès, ${failedUpdates.length} échec(s)`,
            'warning', 6000
          );
        }
      } else {
        throw new Error('Toutes les tentatives ont échoué');
      }

      setSelectedSenders([]);
    } catch (err) {
      console.error('❌ Erreur bulk toggle:', err);
      addNotification(`Erreur: ${err.message}`, 'error', 6000);
    } finally {
      setStatusLoading(false);
    }
  };

  // ============================================
  // CRÉATION
  // ============================================
  const handleCreateSender = async () => {
    const validation = validateSenderID(senderForm.senderId, senderForm.type);
    if (!validation.isValid) {
      addNotification(validation.message, 'error', 6000);
      return;
    }

    setCreateLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      const senderData = { sender_id: senderForm.senderId.toUpperCase() };
      const response = await addSender(token, senderData);

      if (response && response.data) {
        const newSender = {
          id: response.data.id,
          senderId: response.data.sender_id,
          type: senderForm.type,
          statut: response.data.is_active === "YES" ? "Approuvé" : "Non approuvé",
          dateCreation: response.data.reg_date || new Date().toLocaleString('fr-FR'),
          dateApprobation: response.data.date_activation || "",
          utilise: 0,
          description: senderForm.description,
          priorite: senderForm.priorite,
          campagne: senderForm.campagne,
          business_id: response.data.business_id,
          business_name: response.data.business_name,
          sender_key: response.sender_key,
          is_active: response.data.is_active
        };

        setSenderIDs(prev => [newSender, ...prev]);
        setSenderForm({ senderId: '', type: 'Alphanumérique', description: '', priorite: 'Moyenne', campagne: '' });
        setShowCreateModal(false);

        addNotification(
          `Sender ID "${senderForm.senderId}" créé et soumis pour approbation`,
          'success', 6000
        );
      } else {
        throw new Error('Réponse invalide de l\'API');
      }
    } catch (err) {
      console.error('❌ Erreur création:', err);
      addNotification(`Erreur: ${err.message}`, 'error', 8000);
    } finally {
      setCreateLoading(false);
    }
  };

  // ============================================
  // SUPPRESSION
  // ============================================
  const handleConfirmDelete = async () => {
    if (!selectedSender) return;

    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      const response = await deleteSender(token, selectedSender.id);

      if (response && response.code === 200) {
        setSenderIDs(prev => prev.filter(s => s.id !== selectedSender.id));
        addNotification(`Sender ID "${selectedSender.senderId}" supprimé`, 'success', 5000);
      } else {
        throw new Error(response?.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('❌ Erreur suppression:', err);
      addNotification(`Erreur: ${err.message}`, 'error', 6000);
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
      setSelectedSender(null);
    }
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedSenders.length === 0) return;

    setBulkDeleteLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      const deletePromises = selectedSenders.map(senderKey => deleteSender(token, senderKey));
      const results = await Promise.allSettled(deletePromises);

      const successfulDeletes = results.filter(r => r.status === 'fulfilled' && r.value?.code === 200);
      const failedDeletes = results.filter(r => r.status === 'rejected' || r.value?.code !== 200);

      if (failedDeletes.length === 0) {
        setSenderIDs(prev => prev.filter(s => !selectedSenders.includes(s.sender_key)));
        setSelectedSenders([]);
        addNotification(`${selectedSenders.length} Sender ID(s) supprimé(s)`, 'success', 5000);
      } else if (successfulDeletes.length > 0) {
        const successfulKeys = selectedSenders.filter((_, index) =>
          results[index].status === 'fulfilled' && results[index].value?.code === 200
        );
        setSenderIDs(prev => prev.filter(s => !successfulKeys.includes(s.sender_key)));
        setSelectedSenders([]);
        addNotification(`${successfulDeletes.length} succès, ${failedDeletes.length} échec(s)`, 'warning', 6000);
      } else {
        throw new Error('Toutes les tentatives ont échoué');
      }
    } catch (err) {
      console.error('❌ Erreur bulk delete:', err);
      addNotification(`Erreur: ${err.message}`, 'error', 6000);
    } finally {
      setBulkDeleteLoading(false);
      setShowBulkDeleteModal(false);
    }
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handleEditSender = (sender) => {
    setSelectedSender(sender);
    setSenderForm({
      senderId: sender.senderId,
      type: sender.type,
      description: sender.description,
      priorite: sender.priorite,
      campagne: sender.campagne
    });
    setShowEditModal(true);
  };

  const handleUpdateSender = () => {
    const validation = validateSenderID(senderForm.senderId, senderForm.type);
    if (!validation.isValid) {
      addNotification(validation.message, 'error', 6000);
      return;
    }

    setSenderIDs(prev =>
      prev.map(s =>
        s.id === selectedSender.id
          ? {
              ...s,
              senderId: senderForm.senderId.toUpperCase(),
              type: senderForm.type,
              description: senderForm.description,
              priorite: senderForm.priorite,
              campagne: senderForm.campagne
            }
          : s
      )
    );

    setShowEditModal(false);
    setSelectedSender(null);
    setSenderForm({ senderId: '', type: 'Alphanumérique', description: '', priorite: 'Moyenne', campagne: '' });
    addNotification(`Sender ID "${senderForm.senderId}" mis à jour`, 'success', 5000);
  };

  const handleShowDetails = (sender) => {
    setSelectedSender(sender);
    setShowDetailsModal(true);
  };

  const handleDeleteSender = (senderKey) => {
    const senderToDelete = senderIDs.find(s => s.sender_key === senderKey);
    setSelectedSender(senderToDelete);
    setShowDeleteModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedSenders.length === 0) {
      addNotification("Sélectionnez au moins un Sender ID", 'warning', 5000);
      return;
    }
    setShowBulkDeleteModal(true);
  };

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    let filtered = senderIDs.filter(sender => {
      const matchesSearch =
        sender.senderId.toLowerCase().includes(filterText.toLowerCase()) ||
        sender.type.toLowerCase().includes(filterText.toLowerCase()) ||
        sender.description.toLowerCase().includes(filterText.toLowerCase()) ||
        sender.business_name.toLowerCase().includes(filterText.toLowerCase()) ||
        sender.campagne.toLowerCase().includes(filterText.toLowerCase());

      const matchesStatut = selectedStatutFilter === 'all' || sender.statut === selectedStatutFilter;

      return matchesSearch && matchesStatut;
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [filterText, sortField, sortDirection, selectedStatutFilter, senderIDs]);

  // ============================================
  // STATS
  // ============================================
  const stats = useMemo(() => {
    const total = senderIDs.length;
    const approuves = senderIDs.filter(s => s.statut === "Approuvé").length;
    const enAttente = senderIDs.filter(s => s.statut === "En attente" || s.statut === "En vérification").length;
    const rejetes = senderIDs.filter(s => s.statut === "Non approuvé").length;

    return { total, approuves, enAttente, rejetes };
  }, [senderIDs]);

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // ============================================
  // SÉLECTION
  // ============================================
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedSenders(currentItems.map(s => s.sender_key));
    else setSelectedSenders([]);
  };

  const handleSelectSender = (senderKey) => {
    setSelectedSenders(prev =>
      prev.includes(senderKey)
        ? prev.filter(k => k !== senderKey)
        : [...prev, senderKey]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedSenders.length === currentItems.length;
  const isSomeSelected = selectedSenders.length > 0 && selectedSenders.length < currentItems.length;

  // ============================================
  // EXPORT
  // ============================================
  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedSenders.length > 0
      ? senderIDs.filter(sender => selectedSenders.includes(sender.sender_key))
      : filteredAndSortedItems;

    if (!dataToExport.length) {
      addNotification("Aucune donnée à exporter", 'warning', 4000);
      setExportLoading(false);
      return;
    }

    try {
      if (format === "csv") {
        const headers = ["Sender ID", "Type", "Statut", "Description", "Entité", "Date Création"];
        const csvContent = [
          "\uFEFF" + headers.join(";"),
          ...dataToExport.map(s => [
            s.senderId, s.type, s.statut, s.description, s.business_name, s.dateCreation
          ].map(v => `"${v}"`).join(";"))
        ].join("\n");
        downloadFile(csvContent, "text/csv;charset=utf-8;", `export-senderids-${Date.now()}.csv`);
      } else if (format === "excel") {
        const ws = XLSX.utils.json_to_sheet(dataToExport.map(s => ({
          "Sender ID": s.senderId, "Type": s.type, "Statut": s.statut,
          "Description": s.description, "Entite": s.business_name, "Date Création": s.dateCreation
        })));
        ws['!cols'] = [{ wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 25 }, { wch: 10 }, { wch: 15 }, { wch: 12 }, { wch: 20 }, { wch: 20 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sender IDs");
        XLSX.writeFile(wb, `export-senderids-${Date.now()}.xlsx`);
      } else if (format === "json") {
        downloadFile(JSON.stringify(dataToExport, null, 2), "application/json;charset=utf-8;", `export-senderids-${Date.now()}.json`);
      } else if (format === "pdf") {
        generatePDF(dataToExport);
      }
      addNotification(`Export ${format.toUpperCase()} réussi`, 'success', 4000);
    } catch (error) {
      addNotification(`Erreur export: ${error.message}`, 'error', 6000);
    } finally {
      setExportLoading(false);
    }
  };

  const generatePDF = (data) => {
    const doc = new jsPDF();
    const primaryColor = [41, 128, 185];
    const headerColor = [240, 240, 240];
    const alternateColor = [248, 249, 250];

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 25, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("LISTE DES SENDER ID", 105, 15, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Export: ${new Date().toLocaleDateString('fr-FR')}`, 15, 22);
    doc.text(`Total: ${data.length}`, 180, 22, { align: 'right' });

    const margin = 10;
    let yPosition = 35;
    const colWidths = [30, 35, 25, 40, 30, 40];
    const headers = ["SENDER ID", "TYPE", "STATUT", "DESCRIPTION", "ENTITE", "DATE CRÉATION"];

    doc.setFillColor(...headerColor);
    doc.rect(margin, yPosition - 4, 210 - 2 * margin, 8, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");

    let xPosition = margin;
    headers.forEach((header, index) => {
      doc.text(header, xPosition + 2, yPosition);
      xPosition += colWidths[index];
    });
    yPosition += 6;
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");

    data.forEach((item, index) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
        doc.setFillColor(...headerColor);
        doc.rect(margin, yPosition - 4, 210 - 2 * margin, 8, 'F');
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        let newX = margin;
        headers.forEach((header, idx) => {
          doc.text(header, newX + 2, yPosition);
          newX += colWidths[idx];
        });
        yPosition += 6;
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
      }

      if (index % 2 === 0) {
        doc.setFillColor(...alternateColor);
        doc.rect(margin, yPosition - 4, 210 - 2 * margin, 6, 'F');
      }

      const rowData = [
        item.senderId, item.type, item.statut,
        item.description.substring(0, 30) + (item.description.length > 30 ? "..." : ""),
        item.business_name, item.dateCreation
      ];

      let currentX = margin;
      rowData.forEach((cell, cellIndex) => {
        doc.setTextColor(0, 0, 0);
        doc.text(cell, currentX + 2, yPosition);
        currentX += colWidths[cellIndex];
      });
      yPosition += 6;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 275, 210 - margin, 275);
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.text(`Page ${i} sur ${pageCount}`, 105, 282, { align: 'center' });
    }

    doc.save(`sender-ids-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  };

  // ============================================
  // SUB-COMPONENTS
  // ============================================
  const SortableHeader = ({ field, children }) => (
    <th className="sid-sortable-header" onClick={() => handleSort(field)}>
      <span className="sid-sortable-content">
        {children}
        {sortField === field && (
          <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill sid-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  return (
    <div className="sid-page main main-app p-3 p-lg-4">
      {/* ========== TOASTS ========== */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        {notifications.map((notification) => {
          const bgColor = getNotificationBg(notification.type);
          const icon = getNotificationIcon(notification.type);
          return (
            <Toast key={notification.id} show={notification.show} onClose={() => removeNotification(notification.id)} delay={8000} autohide bg={bgColor}>
              <Toast.Header className={`bg-${bgColor} text-white`}>
                <i className={`bi ${icon} me-2`}></i>
                <strong className="me-auto">
                  {notification.type === 'success' ? 'Succès' :
                   notification.type === 'error' ? 'Erreur' :
                   notification.type === 'warning' ? 'Attention' : 'Information'}
                </strong>
                <small>{new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
              </Toast.Header>
              <Toast.Body className="text-white">{notification.message}</Toast.Body>
            </Toast>
          );
        })}
      </ToastContainer>

      {/* ========== HEADER ========== */}
      <div className="sid-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="sid-header-icon">
              <i className="bi bi-send-fill"></i>
            </div>
            <div>
              <h3 className="sid-header-title mb-1">Gestion des Sender ID</h3>
              <p className="sid-header-subtitle mb-0">
                Gérez les identifiants d'expéditeur pour vos campagnes SMS
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {selectedSenders.length > 0 && user?.acc_type === 'business' && (
              <Button className="sid-btn-danger" onClick={handleBulkDelete}>
                <i className="bi bi-trash"></i>
                <span>Supprimer ({selectedSenders.length})</span>
              </Button>
            )}

            <Dropdown>
              <Dropdown.Toggle className="sid-btn-secondary" disabled={exportLoading}>
                <i className={`bi ${exportLoading ? 'bi-arrow-repeat sid-spin' : 'bi-download'}`}></i>
                <span>Exporter</span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="sid-dropdown-menu">
                <Dropdown.Item onClick={() => handleExport('csv')}>
                  <i className="bi bi-file-earmark-spreadsheet text-success me-2"></i>Export CSV
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('excel')}>
                  <i className="bi bi-file-earmark-excel text-success me-2"></i>Export Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('pdf')}>
                  <i className="bi bi-file-earmark-pdf text-danger me-2"></i>Export PDF
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('json')}>
                  <i className="bi bi-file-earmark-code text-warning me-2"></i>Export JSON
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {user?.acc_type === 'business' && (
              <Button className="sid-btn-primary" onClick={() => setShowCreateModal(true)}>
                <i className="bi bi-plus-circle"></i>
                <span>Nouveau Sender ID</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ========== ERROR ========== */}
      {error && (
        <div className="sid-alert sid-alert-danger mb-4">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div className="flex-grow-1">{error}</div>
        </div>
      )}

      {/* ========== BULK ACTIONS (ADMIN) ========== */}
      {user?.acc_type === 'admin' && selectedSenders.length > 0 && (
        <div className="sid-bulk-actions mb-4">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-gear-fill"></i>
            <strong>{selectedSenders.length} Sender ID(s) sélectionné(s)</strong>
          </div>
          <div className="d-flex gap-2">
            <button className="sid-btn-bulk sid-btn-bulk-success" onClick={() => handleBulkToggleStatus(true)} disabled={statusLoading}>
              {statusLoading ? <Spinner animation="border" size="sm" /> : <i className="bi bi-check-circle"></i>}
              Activer
            </button>
            <button className="sid-btn-bulk sid-btn-bulk-danger" onClick={() => handleBulkToggleStatus(false)} disabled={statusLoading}>
              {statusLoading ? <Spinner animation="border" size="sm" /> : <i className="bi bi-x-circle"></i>}
              Désactiver
            </button>
          </div>
        </div>
      )}

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col sm={6} lg={4}>
          <div className="sid-stat-card sid-stat-blue">
            <div className="sid-stat-icon-wrapper sid-stat-icon-blue">
              <i className="bi bi-send-fill"></i>
            </div>
            <div className="sid-stat-content">
              <div className="sid-stat-label">Total Sender ID</div>
              <div className="sid-stat-value">{stats.total.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={6} lg={4}>
          <div className="sid-stat-card sid-stat-green">
            <div className="sid-stat-icon-wrapper sid-stat-icon-green">
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <div className="sid-stat-content">
              <div className="sid-stat-label">Approuvés</div>
              <div className="sid-stat-value">{stats.approuves.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
        <Col sm={12} lg={4}>
          <div className="sid-stat-card sid-stat-amber">
            <div className="sid-stat-icon-wrapper sid-stat-icon-amber">
              <i className="bi bi-clock-fill"></i>
            </div>
            <div className="sid-stat-content">
              <div className="sid-stat-label">En attente</div>
              <div className="sid-stat-value">{stats.enAttente.toLocaleString('fr-FR')}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="sid-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={6} md={12}>
            <div className="sid-search-wrapper">
              <i className="bi bi-search sid-search-icon"></i>
              <input
                type="text"
                className="sid-search-input"
                placeholder="Rechercher par Sender ID, description, campagne..."
                value={filterText}
                onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
              />
              {filterText && (
                <button className="sid-search-clear" onClick={() => setFilterText('')}>
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="sid-select-full"
              value={selectedStatutFilter}
              onChange={(e) => { setSelectedStatutFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="all">Tous les statuts</option>
              {statutsDisponibles.map(statut => (
                <option key={statut} value={statut}>{statut}</option>
              ))}
            </select>
          </Col>
          <Col lg={3} md={6}>
            <div className="d-flex gap-2">
              <button className="sid-btn-secondary w-100" onClick={loadSenders}>
                <i className="bi bi-arrow-clockwise"></i>
                <span>Actualiser</span>
              </button>
            </div>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="sid-table-card">
        {loading ? (
          <div className="sid-loading">
            <div className="sid-loading-spinner"></div>
            <div className="sid-loading-text">Chargement des Sender ID...</div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="sid-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <div className="sid-checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="sid-checkbox"
                          checked={isAllSelected}
                          ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    <SortableHeader field="senderId">
                      <i className="bi bi-hash me-1"></i>Sender ID
                    </SortableHeader>
                    <SortableHeader field="type">
                      <i className="bi bi-tag me-1"></i>Type
                    </SortableHeader>
                    <th>
                      <i className="bi bi-file-text me-1"></i>Description
                    </th>
                    <SortableHeader field="entite">
                      <i className="bi bi-building me-1"></i>Entité
                    </SortableHeader>
                    <SortableHeader field="statut">
                      <i className="bi bi-flag me-1"></i>Statut
                    </SortableHeader>
                    <SortableHeader field="dateCreation">
                      <i className="bi bi-calendar me-1"></i>Créé le
                    </SortableHeader>
                    <th className="text-end" style={{ width: '120px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((sender) => {
                      const statusConf = getStatusConfig(sender.statut);
                      return (
                        <tr key={sender.id} className="sid-row">
                          <td>
                            <div className="sid-checkbox-wrapper">
                              <input
                                type="checkbox"
                                className="sid-checkbox"
                                checked={selectedSenders.includes(sender.sender_key)}
                                onChange={() => handleSelectSender(sender.sender_key)}
                              />
                            </div>
                          </td>
                          <td>
                            <code className="sid-code">{sender.senderId}</code>
                          </td>
                          <td>
                            <span className={`sid-type-badge sid-type-${sender.type === "Alphanumérique" ? "blue" : sender.type === "Numérique" ? "violet" : "amber"}`}>
                              {sender.type}
                            </span>
                          </td>
                          <td>
                            <span className="sid-description" title={sender.description}>
                              {sender.senderId} - {sender.type}
                            </span>
                          </td>
                          <td>
                            <span className="sid-entity">
                              <i className="bi bi-building"></i>
                              {sender.business_name || '—'}
                            </span>
                          </td>
                          <td>
                            <span
                              className="sid-status-badge"
                              style={{ color: statusConf.color, backgroundColor: statusConf.bg }}
                            >
                              <i className={`bi ${statusConf.icon}`}></i>
                              {statusConf.label}
                            </span>
                          </td>
                          <td>
                            <span className="sid-date-cell">{sender.dateCreation}</span>
                          </td>
                          <td className="text-end">
                            <div className="sid-actions">
                              {user?.acc_type === 'business' && (
                                <button
                                  className="sid-action-btn sid-action-delete"
                                  onClick={() => handleDeleteSender(sender.sender_key)}
                                  title="Supprimer"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}

                              {user?.acc_type === 'admin' && (
                                <button
                                  className={`sid-action-btn ${sender.is_active === 'YES' || sender.is_active === true ? 'sid-action-delete' : 'sid-action-edit'}`}
                                  onClick={() => prepareToggleStatus(sender.sender_key, sender.is_active)}
                                  disabled={statusLoading}
                                  title={sender.is_active === 'YES' || sender.is_active === true ? "Désactiver" : "Activer"}
                                >
                                  {statusLoading ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : sender.is_active === 'YES' || sender.is_active === true ? (
                                    <i className="bi bi-x-circle"></i>
                                  ) : (
                                    <i className="bi bi-check-circle"></i>
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="sid-empty-state">
                          <div className="sid-empty-icon">
                            <i className="bi bi-send"></i>
                          </div>
                          <h5 className="sid-empty-title">Aucun Sender ID trouvé</h5>
                          <p className="sid-empty-text">
                            {filterText || selectedStatutFilter !== 'all'
                              ? "Aucun Sender ID ne correspond à vos critères"
                              : "Commencez par créer votre premier Sender ID"}
                          </p>
                          {(filterText || selectedStatutFilter !== 'all') ? (
                            <button className="sid-btn-empty-reset" onClick={() => { setFilterText(''); setSelectedStatutFilter('all'); }}>
                              <i className="bi bi-arrow-clockwise me-2"></i>
                              Réinitialiser
                            </button>
                          ) : user?.acc_type === 'business' && (
                            <button className="sid-btn-empty-reset" onClick={() => setShowCreateModal(true)}>
                              <i className="bi bi-plus-circle me-2"></i>
                              Créer un Sender ID
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {filteredAndSortedItems.length > 0 && totalPages > 1 && (
              <div className="sid-pagination">
                <div className="sid-pagination-info">
                  <i className="bi bi-info-circle"></i>
                  Affichage <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
                  {selectedSenders.length > 0 && (
                    <span className="sid-pagination-selected">
                      • <strong>{selectedSenders.length}</strong> sélectionné{selectedSenders.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="sid-pagination-controls">
                  <button className="sid-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button className="sid-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                    <i className="bi bi-chevron-left"></i>
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const pageNum = currentPage <= 3
                      ? index + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + index
                        : currentPage - 2 + index;

                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          className={`sid-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}

                  <button className="sid-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button className="sid-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ============================================
          MODAL CRÉATION
          ============================================ */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered size="lg" className="sid-modal">
        <div className="sid-modal-header sid-modal-header-success">
          <div className="d-flex align-items-center gap-3">
            <div className="sid-modal-icon">
              <i className="bi bi-plus-circle-fill"></i>
            </div>
            <div>
              <h5 className="sid-modal-title mb-0">Nouveau Sender ID</h5>
              <small className="sid-modal-subtitle">Créer un identifiant d'expéditeur</small>
            </div>
          </div>
          <button className="sid-modal-close" onClick={() => setShowCreateModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="sid-modal-body">
          <Row className="g-3">
            <Col md={6}>
              <div className="sid-form-group">
                <label className="sid-form-label">Type de Sender ID</label>
                <select
                  className="sid-select"
                  value={senderForm.type}
                  onChange={(e) => setSenderForm({ ...senderForm, type: e.target.value })}
                >
                  {typesSenderID.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <div className="sid-form-hint">
                  <i className="bi bi-info-circle"></i>
                  Choisissez le type approprié
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="sid-form-group">
                <label className="sid-form-label">
                  Sender ID <span className="sid-required">*</span>
                </label>
                <input
                  type="text"
                  className="sid-input sid-input-mono"
                  placeholder={
                    senderForm.type === "Alphanumérique" ? "MONENTREPRISE" :
                    senderForm.type === "Numérique" ? "24101234567" :
                    "12345"
                  }
                  value={senderForm.senderId}
                  onChange={(e) => setSenderForm({ ...senderForm, senderId: e.target.value.toUpperCase() })}
                />
                <div className="sid-form-hint">
                  <i className="bi bi-info-circle"></i>
                  {senderForm.type === "Alphanumérique" && "3-11 caractères (lettres, chiffres, _)"}
                  {senderForm.type === "Numérique" && "Doit commencer par 241 (11 chiffres)"}
                  {senderForm.type === "Court code" && "Code court (max 5 caractères)"}
                </div>
              </div>
            </Col>
          </Row>

          <div className="sid-info-box mt-3">
            <i className="bi bi-info-circle-fill"></i>
            <div>
              <strong>Informations importantes</strong>
              <ul className="mb-0 small mt-2">
                {senderForm.type === "Alphanumérique" && (
                  <>
                    <li>Le Sender ID doit être unique et identifiable</li>
                    <li>Utilisez le nom de votre entreprise</li>
                  </>
                )}
                {senderForm.type === "Numérique" && (
                  <>
                    <li>Doit obligatoirement commencer par 241</li>
                    <li>Format: 241 + 8 chiffres</li>
                  </>
                )}
                {senderForm.type === "Court code" && (
                  <>
                    <li>Code court pour campagnes spécifiques</li>
                    <li>Limité à 5 caractères</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </Modal.Body>

        <div className="sid-modal-footer">
          <button className="sid-btn-cancel" onClick={() => setShowCreateModal(false)}>
            Annuler
          </button>
          <button
            className="sid-btn-primary"
            onClick={handleCreateSender}
            disabled={createLoading || !senderForm.senderId.trim()}
          >
            {createLoading ? (
              <>
                <Spinner animation="border" size="sm" />
                Création...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg"></i>
                Créer le Sender ID
              </>
            )}
          </button>
        </div>
      </Modal>

      {/* ============================================
          MODAL SUPPRESSION
          ============================================ */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="sid-modal">
        <Modal.Body className="sid-delete-body">
          <div className="sid-delete-icon-wrapper">
            <div className="sid-delete-icon">
              <i className="bi bi-trash"></i>
            </div>
          </div>
          <h4 className="sid-delete-title">Confirmer la suppression</h4>
          <p className="sid-delete-text">
            Êtes-vous sûr de vouloir supprimer ce Sender ID ?
          </p>

          {selectedSender && (
            <div className="sid-delete-item">
              <div className="sid-delete-item-icon">
                <i className="bi bi-send-fill"></i>
              </div>
              <div className="sid-delete-item-content">
                <div className="sid-delete-item-label">Sender ID</div>
                <div className="sid-delete-item-value">{selectedSender.senderId}</div>
              </div>
            </div>
          )}

          <div className="sid-delete-warning">
            <i className="bi bi-exclamation-triangle"></i>
            Cette action est irréversible
          </div>

          <div className="sid-delete-actions">
            <button className="sid-btn-cancel sid-btn-flex" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
              <i className="bi bi-x-lg"></i>
              Annuler
            </button>
            <button className="sid-btn-danger sid-btn-flex" onClick={handleConfirmDelete} disabled={deleteLoading}>
              {deleteLoading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Suppression...
                </>
              ) : (
                <>
                  <i className="bi bi-trash"></i>
                  Supprimer
                </>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ============================================
          MODAL SUPPRESSION MULTIPLE
          ============================================ */}
      <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)} centered className="sid-modal">
        <Modal.Body className="sid-delete-body">
          <div className="sid-delete-icon-wrapper">
            <div className="sid-delete-icon">
              <i className="bi bi-trash"></i>
            </div>
          </div>
          <h4 className="sid-delete-title">Suppression multiple</h4>
          <p className="sid-delete-text">
            Vous êtes sur le point de supprimer <strong>{selectedSenders.length} Sender ID(s)</strong>.
          </p>

          <div className="sid-delete-warning">
            <i className="bi bi-exclamation-triangle"></i>
            Cette action est irréversible
          </div>

          <div className="sid-delete-actions">
            <button className="sid-btn-cancel sid-btn-flex" onClick={() => setShowBulkDeleteModal(false)} disabled={bulkDeleteLoading}>
              <i className="bi bi-x-lg"></i>
              Annuler
            </button>
            <button className="sid-btn-danger sid-btn-flex" onClick={handleConfirmBulkDelete} disabled={bulkDeleteLoading}>
              {bulkDeleteLoading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Suppression...
                </>
              ) : (
                <>
                  <i className="bi bi-trash"></i>
                  Supprimer ({selectedSenders.length})
                </>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ============================================
          MODAL CHANGEMENT STATUT
          ============================================ */}
      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered className="sid-modal">
        <div className={`sid-modal-header ${pendingStatusChange?.newStatus === "Approuvé" ? "sid-modal-header-success" : "sid-modal-header-danger"}`}>
          <div className="d-flex align-items-center gap-3">
            <div className="sid-modal-icon">
              <i className={`bi ${pendingStatusChange?.newStatus === "Approuvé" ? "bi-check-circle-fill" : "bi-x-circle-fill"}`}></i>
            </div>
            <div>
              <h5 className="sid-modal-title mb-0">Changement de statut</h5>
              <small className="sid-modal-subtitle">Confirmer l'action</small>
            </div>
          </div>
          <button className="sid-modal-close" onClick={() => setShowStatusModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="sid-modal-body">
          <div className="text-center mb-4">
            <h5 className="mb-3">
              {pendingStatusChange?.newStatus === "Approuvé" ? "Activer" : "Désactiver"} ce Sender ID ?
            </h5>

            {selectedSender && pendingStatusChange && (
              <div className="sid-status-change">
                <div className="sid-status-change-item">
                  <span className="sid-status-change-label">Sender ID</span>
                  <code>{selectedSender.senderId}</code>
                </div>
                <div className="sid-status-change-arrow">
                  <div className="sid-status-change-from">
                    <span className="sid-status-badge" style={{ color: getStatusConfig(selectedSender.statut).color, backgroundColor: getStatusConfig(selectedSender.statut).bg }}>
                      <i className={`bi ${getStatusConfig(selectedSender.statut).icon}`}></i>
                      {getStatusConfig(selectedSender.statut).label}
                    </span>
                  </div>
                  <i className="bi bi-arrow-right"></i>
                  <div className="sid-status-change-to">
                    <span className="sid-status-badge" style={{ color: getStatusConfig(pendingStatusChange.newStatus).color, backgroundColor: getStatusConfig(pendingStatusChange.newStatus).bg }}>
                      <i className={`bi ${getStatusConfig(pendingStatusChange.newStatus).icon}`}></i>
                      {getStatusConfig(pendingStatusChange.newStatus).label}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className={`sid-alert sid-alert-${pendingStatusChange?.newStatus === "Approuvé" ? "success" : "danger"} mt-3`}>
              <i className="bi bi-exclamation-triangle-fill"></i>
              <div>
                <strong>Conséquences :</strong>
                <ul className="mb-0 mt-2 small">
                  {pendingStatusChange?.newStatus === "Approuvé" ? (
                    <>
                      <li>Le Sender ID pourra être utilisé pour envoyer des SMS</li>
                      <li>Les campagnes pourront être exécutées</li>
                    </>
                  ) : (
                    <>
                      <li>Le Sender ID ne pourra plus envoyer de SMS</li>
                      <li>Les campagnes seront suspendues</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </Modal.Body>

        <div className="sid-modal-footer">
          <button className="sid-btn-cancel" onClick={() => setShowStatusModal(false)} disabled={statusLoading}>
            Annuler
          </button>
          <button
            className={pendingStatusChange?.newStatus === "Approuvé" ? "sid-btn-success" : "sid-btn-danger"}
            onClick={executeToggleStatus}
            disabled={statusLoading}
          >
            {statusLoading ? (
              <>
                <Spinner animation="border" size="sm" />
                Traitement...
              </>
            ) : (
              <>
                <i className={`bi ${pendingStatusChange?.newStatus === "Approuvé" ? "bi-check-circle" : "bi-x-circle"}`}></i>
                Confirmer
              </>
            )}
          </button>
        </div>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .sid-page { background: #f8fafc; min-height: 100vh; }
        .sid-page * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

        /* ========== HEADER ========== */
        .sid-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px; padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .sid-header-icon {
          width: 56px; height: 56px; border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .sid-header-title { font-weight: 700; color: #0f172a; font-size: 1.5rem; letter-spacing: -0.02em; }
        .sid-header-subtitle { color: #64748b; font-size: 0.875rem; }

        /* ========== BUTTONS ========== */
        .sid-btn-primary, .sid-btn-secondary, .sid-btn-cancel, .sid-btn-danger, .sid-btn-success {
          display: inline-flex; align-items: center; justify-content: center;
          gap: 8px; padding: 11px 20px; border-radius: 10px;
          font-weight: 600; font-size: 0.875rem; border: none;
          transition: all 0.2s; cursor: pointer; text-decoration: none;
        }
        .sid-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white;
        }
        .sid-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }
        .sid-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .sid-btn-secondary {
          background: white; border: 1px solid #e2e8f0; color: #334155;
        }
        .sid-btn-secondary:hover:not(:disabled) { background: #f8fafc; border-color: #cbd5e1; }
        .sid-btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); color: white;
        }
        .sid-btn-danger:hover:not(:disabled) {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(239, 68, 68, 0.4);
        }
        .sid-btn-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white;
        }
        .sid-btn-success:hover:not(:disabled) {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(16, 185, 129, 0.4);
        }
        .sid-btn-cancel {
          background: white; border: 1px solid #e2e8f0; color: #334155;
        }
        .sid-btn-cancel:hover { background: #f1f5f9; }
        .sid-btn-flex { flex: 1; }
        .sid-dropdown-menu {
          border: 1px solid #e2e8f0; border-radius: 10px;
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.1); padding: 6px;
        }
        .sid-dropdown-menu .dropdown-item {
          border-radius: 6px; padding: 8px 12px; font-size: 0.85rem;
          display: flex; align-items: center;
        }
        .sid-dropdown-menu .dropdown-item:hover { background: #f1f5f9; }

        /* ========== BULK ACTIONS ========== */
        .sid-bulk-actions {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; background: white;
          border-radius: 12px; border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .sid-bulk-actions > div:first-child {
          display: flex; align-items: center; gap: 10px;
          color: #3b82f6; font-weight: 600;
        }
        .sid-btn-bulk {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px; border-radius: 8px;
          font-weight: 600; font-size: 0.8rem;
          border: none; cursor: pointer; transition: all 0.2s;
        }
        .sid-btn-bulk-success { background: #d1fae5; color: #059669; }
        .sid-btn-bulk-success:hover:not(:disabled) { background: #10b981; color: white; }
        .sid-btn-bulk-danger { background: #fee2e2; color: #dc2626; }
        .sid-btn-bulk-danger:hover:not(:disabled) { background: #ef4444; color: white; }

        /* ========== ALERTS ========== */
        .sid-alert {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 18px; border-radius: 12px;
          font-size: 0.85rem; font-weight: 500;
        }
        .sid-alert > i { font-size: 1.3rem; flex-shrink: 0; }
        .sid-alert-danger {
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          color: #991b1b; border-left: 4px solid #ef4444;
        }
        .sid-alert-success {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          color: #065f46; border-left: 4px solid #10b981;
        }
        .sid-info-box {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 14px 18px;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          color: #1e40af; border-radius: 12px; border-left: 4px solid #3b82f6;
          font-size: 0.85rem;
        }
        .sid-info-box > i { color: #3b82f6; font-size: 1.3rem; flex-shrink: 0; }

        /* ========== STATS ========== */
        .sid-stat-card {
          display: flex; align-items: center; gap: 14px;
          background: white; border-radius: 14px; padding: 18px 20px;
          border: 1px solid #e2e8f0; transition: all 0.25s;
        }
        .sid-stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08); }
        .sid-stat-icon-wrapper {
          width: 46px; height: 46px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px; flex-shrink: 0;
        }
        .sid-stat-icon-blue   { background: #eff6ff; color: #3b82f6; }
        .sid-stat-icon-green  { background: #d1fae5; color: #10b981; }
        .sid-stat-icon-amber  { background: #fef3c7; color: #f59e0b; }
        .sid-stat-content { min-width: 0; flex-grow: 1; }
        .sid-stat-label {
          font-size: 0.72rem; color: #64748b; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 3px;
        }
        .sid-stat-value {
          font-size: 1.5rem; font-weight: 700; color: #0f172a;
          letter-spacing: -0.02em; line-height: 1;
        }

        /* ========== FILTRES ========== */
        .sid-filters { background: white; border-radius: 14px; padding: 16px 20px; border: 1px solid #e2e8f0; }
        .sid-search-wrapper { position: relative; display: flex; align-items: center; }
        .sid-search-icon {
          position: absolute; left: 14px; color: #94a3b8;
          font-size: 1.05rem; pointer-events: none;
        }
        .sid-search-input {
          width: 100%; padding: 11px 40px 11px 42px;
          border: 1px solid #e2e8f0; border-radius: 10px;
          font-size: 0.875rem; background: #f8fafc;
          transition: all 0.2s; outline: none; font-family: inherit;
        }
        .sid-search-input:focus {
          background: white; border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .sid-search-clear {
          position: absolute; right: 8px; background: none; border: none;
          color: #94a3b8; font-size: 1rem; padding: 6px 10px;
          cursor: pointer; border-radius: 6px; transition: all 0.15s;
        }
        .sid-search-clear:hover { background: #fee2e2; color: #ef4444; }
        .sid-select-full, .sid-select {
          width: 100%; padding: 11px 14px;
          border: 1px solid #e2e8f0; border-radius: 10px;
          font-size: 0.875rem; font-weight: 500;
          background: #f8fafc; color: #334155;
          outline: none; cursor: pointer; font-family: inherit;
        }
        .sid-select-full:focus, .sid-select:focus {
          background: white; border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        /* ========== TABLE ========== */
        .sid-table-card {
          background: white; border-radius: 16px;
          border: 1px solid #e2e8f0; overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .sid-table { font-size: 0.875rem; }
        .sid-table thead th {
          background: #f8fafc; color: #475569;
          font-weight: 700; font-size: 0.7rem;
          text-transform: uppercase; letter-spacing: 0.05em;
          padding: 16px; border-bottom: 1px solid #e2e8f0;
          border-top: none; white-space: nowrap;
        }
        .sid-table tbody td {
          padding: 16px; vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        .sid-row { transition: background-color 0.15s; }
        .sid-row:hover { background-color: #f8fafc; }
        .sid-sortable-header { cursor: pointer; user-select: none; }
        .sid-sortable-header:hover { color: #3b82f6; }
        .sid-sortable-content { display: inline-flex; align-items: center; gap: 6px; }
        .sid-sort-icon { color: #3b82f6; font-size: 0.75rem; }

        .sid-checkbox-wrapper { display: flex; align-items: center; justify-content: center; }
        .sid-checkbox {
          width: 18px; height: 18px; border-radius: 5px;
          border: 1.5px solid #cbd5e1; cursor: pointer;
          accent-color: #3b82f6;
        }

        .sid-code {
          font-size: 0.78rem; color: #1e40af;
          background: #eff6ff; padding: 4px 10px;
          border-radius: 6px; font-weight: 700;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
        }

        .sid-type-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 11px; border-radius: 6px;
          font-size: 0.72rem; font-weight: 700;
          white-space: nowrap;
        }
        .sid-type-blue   { background: #eff6ff; color: #3b82f6; }
        .sid-type-violet { background: #ede9fe; color: #8b5cf6; }
        .sid-type-amber  { background: #fef3c7; color: #d97706; }

        .sid-description {
          color: #64748b; font-size: 0.82rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          max-width: 250px;
        }

        .sid-entity {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 0.8rem; color: #475569;
        }
        .sid-entity > i { color: #94a3b8; font-size: 0.85rem; }

        .sid-status-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 11px; border-radius: 6px;
          font-weight: 700; font-size: 0.72rem;
          white-space: nowrap;
        }

        .sid-date-cell {
          color: #64748b; font-size: 0.78rem;
          font-variant-numeric: tabular-nums;
        }

        .sid-actions { display: inline-flex; gap: 6px; justify-content: flex-end; }
        .sid-action-btn {
          width: 34px; height: 34px; border-radius: 8px;
          border: 1px solid #e2e8f0; background: white;
          color: #64748b; display: inline-flex;
          align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.15s; font-size: 0.9rem;
        }
        .sid-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .sid-action-delete:hover:not(:disabled) {
          background: #ef4444; border-color: #ef4444; color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(239, 68, 68, 0.4);
        }
        .sid-action-edit:hover:not(:disabled) {
          background: #10b981; border-color: #10b981; color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px -2px rgba(16, 185, 129, 0.4);
        }

        /* ========== LOADING / EMPTY ========== */
        .sid-loading { padding: 80px 20px; text-align: center; }
        .sid-loading-spinner {
          width: 48px; height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: sid-spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .sid-loading-text { color: #64748b; font-size: 0.875rem; font-weight: 500; }
        .sid-empty-state { padding: 60px 20px; text-align: center; }
        .sid-empty-icon {
          width: 80px; height: 80px; border-radius: 50%;
          background: #f1f5f9; color: #94a3b8;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 36px; margin-bottom: 20px;
        }
        .sid-empty-title { color: #334155; font-weight: 700; margin-bottom: 8px; }
        .sid-empty-text { color: #94a3b8; margin-bottom: 20px; }
        .sid-btn-empty-reset {
          display: inline-flex; align-items: center;
          background: white; color: #3b82f6;
          border: 1px solid #bfdbfe; border-radius: 8px;
          font-weight: 600; padding: 8px 16px;
          cursor: pointer; transition: all 0.2s;
        }
        .sid-btn-empty-reset:hover { background: #3b82f6; color: white; border-color: #3b82f6; }

        /* ========== PAGINATION ========== */
        .sid-pagination {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 24px; background: #f8fafc;
          border-top: 1px solid #e2e8f0; flex-wrap: wrap; gap: 12px;
        }
        .sid-pagination-info {
          font-size: 0.8rem; color: #64748b;
          display: flex; align-items: center; gap: 6px;
        }
        .sid-pagination-info strong { color: #0f172a; }
        .sid-pagination-selected { color: #3b82f6; margin-left: 8px; }
        .sid-pagination-controls { display: flex; align-items: center; gap: 6px; }
        .sid-page-btn {
          min-width: 34px; height: 34px; padding: 0 10px;
          border-radius: 8px; border: 1px solid #e2e8f0;
          background: white; color: #475569;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.15s;
          font-size: 0.8rem; font-weight: 600;
        }
        .sid-page-btn:hover:not(:disabled) { background: #3b82f6; color: white; border-color: #3b82f6; }
        .sid-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .sid-page-btn.active { background: #3b82f6; color: white; border-color: #3b82f6; }

        /* ========== MODAL ========== */
        .sid-modal .modal-content { border: none; border-radius: 16px; overflow: hidden; }
        .sid-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 24px; color: white;
        }
        .sid-modal-header-success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
        .sid-modal-header-danger  { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
        .sid-modal-icon {
          width: 44px; height: 44px; border-radius: 10px;
          background: rgba(255,255,255,0.2);
          display: flex; align-items: center; justify-content: center;
          font-size: 20px;
        }
        .sid-modal-title { font-weight: 700; letter-spacing: -0.01em; margin: 0; }
        .sid-modal-subtitle { color: rgba(255,255,255,0.85); font-size: 0.8rem; }
        .sid-modal-close {
          background: rgba(255,255,255,0.1); border: none; color: white;
          width: 36px; height: 36px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.2s; font-size: 1rem;
        }
        .sid-modal-close:hover { background: rgba(255,255,255,0.2); transform: rotate(90deg); }
        .sid-modal-body { padding: 24px; background: #f8fafc; max-height: 65vh; overflow-y: auto; }
        .sid-modal-footer {
          padding: 16px 24px; background: white;
          border-top: 1px solid #e2e8f0;
          display: flex; justify-content: flex-end; gap: 10px;
        }

        /* Form */
        .sid-form-group { margin-bottom: 4px; }
        .sid-form-label {
          display: block; font-weight: 700; color: #0f172a;
          font-size: 0.82rem; margin-bottom: 8px;
        }
        .sid-required { color: #ef4444; }
        .sid-input {
          width: 100%; padding: 11px 14px;
          border: 2px solid #e2e8f0; border-radius: 10px;
          font-size: 0.875rem; color: #0f172a;
          background: white; transition: all 0.2s;
          outline: none; font-family: inherit;
        }
        .sid-input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }
        .sid-input-mono {
          font-family: 'SF Mono', Menlo, Monaco, monospace;
          font-weight: 700;
          letter-spacing: 0.05em;
        }
        .sid-form-hint {
          margin-top: 6px; font-size: 0.72rem;
          color: #94a3b8; display: flex; align-items: center; gap: 4px;
        }
        .sid-form-hint i { color: #3b82f6; }

        /* Status change */
        .sid-status-change {
          background: white; border-radius: 12px;
          padding: 16px; border: 1px solid #e2e8f0;
        }
        .sid-status-change-item {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 12px; padding-bottom: 12px;
          border-bottom: 1px solid #f1f5f9;
        }
        .sid-status-change-label {
          font-size: 0.72rem; color: #94a3b8;
          font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .sid-status-change-item code {
          font-family: 'SF Mono', Menlo, monospace;
          font-size: 0.8rem; color: #1e40af;
          background: #eff6ff; padding: 3px 8px; border-radius: 5px;
        }
        .sid-status-change-arrow {
          display: flex; align-items: center; justify-content: center;
          gap: 12px; flex-wrap: wrap;
        }
        .sid-status-change-from, .sid-status-change-to { flex: 1; text-align: center; }

        /* Delete modal */
        .sid-delete-body { padding: 32px 24px 24px; text-align: center; }
        .sid-delete-icon-wrapper { margin-bottom: 20px; }
        .sid-delete-icon {
          width: 80px; height: 80px; border-radius: 50%;
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626; display: inline-flex;
          align-items: center; justify-content: center;
          font-size: 36px;
          box-shadow: 0 12px 24px -8px rgba(220, 38, 38, 0.3);
        }
        .sid-delete-title {
          font-weight: 700; color: #0f172a;
          margin-bottom: 12px; font-size: 1.25rem;
        }
        .sid-delete-text { color: #64748b; margin-bottom: 20px; font-size: 0.875rem; }
        .sid-delete-item {
          display: flex; align-items: center; gap: 14px;
          background: #f8fafc; border-radius: 12px;
          padding: 16px 20px; margin-bottom: 16px;
          border: 1px solid #e2e8f0; text-align: left;
        }
        .sid-delete-item-icon {
          width: 48px; height: 48px; border-radius: 12px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white; display: flex;
          align-items: center; justify-content: center;
          font-size: 20px; flex-shrink: 0;
        }
        .sid-delete-item-content { min-width: 0; flex-grow: 1; }
        .sid-delete-item-label {
          font-size: 0.72rem; color: #94a3b8;
          font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.05em; margin-bottom: 4px;
        }
        .sid-delete-item-value {
          font-weight: 700; color: #0f172a;
          font-size: 1rem; font-family: 'SF Mono', Menlo, Monaco, monospace;
          word-break: break-all;
        }
        .sid-delete-warning {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px; background: #fef2f2;
          color: #dc2626; border-radius: 8px;
          font-size: 0.78rem; font-weight: 700; margin-bottom: 20px;
        }
        .sid-delete-actions { display: flex; gap: 10px; }

        /* ========== ANIMATIONS ========== */
        .sid-spin { animation: sid-spin 1s linear infinite; display: inline-block; }
        @keyframes sid-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .sid-header { padding: 20px; }
          .sid-header-title { font-size: 1.2rem; }
          .sid-pagination { flex-direction: column; }
          .sid-stat-value { font-size: 1.25rem; }
          .sid-modal-body { padding: 16px; }
          .sid-delete-actions { flex-direction: column; }
          .sid-bulk-actions { flex-direction: column; gap: 12px; }
        }
      `}</style>
    </div>
  );
}